/**
 * FERP Portal - Feature Catalog Hook
 *
 * Builds a browsable catalog of all routable features from TenantSubgraphConfig
 * and manages per-tenant layer visibility overrides in localStorage.
 *
 * The Architect view uses this to browse all available pages/features
 * and selectively promote them into Admin or User views.
 *
 * FERP Principles:
 * - Data-driven: Feature list derived from backend SubgraphMetadata
 * - No sensitive data: Only stores route paths and layer arrays
 * - Graceful degradation: No-op if localStorage unavailable
 * - Backend is source of truth: Overrides are additive, defaults always from backend
 *
 * localStorage key: ferp:catalog:{tenantId}
 *
 * Future (Phase 2): Backend persistence via TenantConfig entity in DynamoDB
 */

import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/auth';
import {
  useTenantSubgraphConfig,
  type ViewLayer,
  type SubgraphRoute,
  type SubgraphNavItem,
  type SubgraphInfo,
} from './useTenantSubgraphConfig';

// ============================================================================
// Types
// ============================================================================

export interface CatalogFeature {
  /** Unique ID — the route path */
  id: string;
  /** Human-readable name from route title or nav item label */
  title: string;
  /** Description from subgraph info */
  description: string;
  /** Icon name from nav item */
  icon: string;
  /** Category — subgraph display name */
  category: string;
  /** Default layer from backend metadata */
  defaultLayer: ViewLayer;
  /** Current effective visibility (default + overrides) */
  visibleIn: ViewLayer[];
  /** Source subgraph ID */
  source: string;
  /** Component name from COMPONENT_MAP registry */
  componentName: string;
  /** Whether this route requires life-critical auth */
  requiresLifeCriticalAuth: boolean;
  /** Associated nav item IDs (for sidebar visibility) */
  navItemIds: string[];
}

export interface FeatureLayerOverrides {
  /** Schema version for future migrations */
  version: 1;
  /** Map of route path to additional layers where feature should be visible */
  overrides: Record<string, ViewLayer[]>;
  /** ISO 8601 timestamp of last update */
  updatedAt: string;
  /** User ID who last updated */
  updatedBy: string;
}

export interface UseFeatureCatalogReturn {
  /** All features available to this tenant */
  features: CatalogFeature[];
  /** All unique categories (subgraph display names) */
  categories: string[];
  /** Toggle a feature's visibility in a specific layer */
  toggleFeatureLayer: (featureId: string, layer: ViewLayer, enabled: boolean) => void;
  /** Check if a feature is visible in a given layer (respecting overrides) */
  isVisibleInLayer: (featureId: string, layer: ViewLayer) => boolean;
  /** Reset all overrides to defaults */
  resetOverrides: () => void;
  /** Current overrides (for debugging/export) */
  overrides: FeatureLayerOverrides | null;
  /** Whether config is still loading */
  loading: boolean;
  /** Count of features with overrides */
  overrideCount: number;
}

// ============================================================================
// localStorage helpers (following useEntityAccessTracker pattern)
// ============================================================================

const STORAGE_PREFIX = 'ferp:catalog';

function buildStorageKey(tenantId: string): string {
  return `${STORAGE_PREFIX}:${tenantId}`;
}

function readOverrides(tenantId: string): FeatureLayerOverrides | null {
  try {
    const raw = localStorage.getItem(buildStorageKey(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FeatureLayerOverrides;
    if (parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeOverrides(tenantId: string, data: FeatureLayerOverrides): void {
  try {
    localStorage.setItem(buildStorageKey(tenantId), JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

function clearOverrides(tenantId: string): void {
  try {
    localStorage.removeItem(buildStorageKey(tenantId));
  } catch {
    // fail silently
  }
}

// ============================================================================
// Feature building helpers
// ============================================================================

const ALL_LAYERS: ViewLayer[] = ['User', 'Admin', 'Architect'];

function normalizeLayer(layer: string): ViewLayer {
  const upper = layer.toUpperCase();
  if (upper === 'USER') return 'User';
  if (upper === 'ADMIN') return 'Admin';
  return 'Architect';
}

/**
 * Build CatalogFeature[] by joining routes, nav items, and subgraph info.
 * Each route becomes one feature. Nav items are matched by path.
 */
function buildFeatures(
  routes: SubgraphRoute[],
  navItems: SubgraphNavItem[],
  subgraphs: SubgraphInfo[],
  overrides: FeatureLayerOverrides | null,
): CatalogFeature[] {
  // Index nav items by path for fast lookup
  const navByPath = new Map<string, SubgraphNavItem[]>();
  for (const nav of navItems) {
    if (!nav.path) continue;
    const existing = navByPath.get(nav.path) ?? [];
    existing.push(nav);
    navByPath.set(nav.path, existing);
  }

  // Index subgraphs by ID
  const subgraphMap = new Map<string, SubgraphInfo>();
  for (const sg of subgraphs) {
    subgraphMap.set(sg.subgraphId, sg);
  }

  // Build features from routes (routes are the canonical feature list)
  const features: CatalogFeature[] = [];

  for (const route of routes) {
    // Skip routes without a component (redirect-only routes)
    if (!route.component) continue;

    // Find matching nav items
    const matchingNavs = navByPath.get(route.path) ?? [];

    // Determine source subgraph from path prefix
    // Routes follow pattern: /{subgraphId}/... or /ferp-core/...
    const pathParts = route.path.split('/').filter(Boolean);
    const source = pathParts[0] ?? 'ferp-core';
    const subgraph = subgraphMap.get(source);

    // Build title: prefer nav label, fall back to route title
    const primaryNav = matchingNavs[0];
    const title = primaryNav?.label ?? route.title ?? route.component;

    // Build description from subgraph
    const description = subgraph?.description ?? '';

    // Build icon from nav item
    const icon = primaryNav?.icon ?? 'IconFile';

    // Category = subgraph display name
    const category = subgraph?.displayName ?? source;

    // Default layer from route
    const defaultLayer = normalizeLayer(route.layer as string);

    // Effective visibility: default + overrides
    const overrideLayersRaw = overrides?.overrides[route.path];
    const overrideLayers = overrideLayersRaw ?? [];
    const visibleIn = Array.from(new Set([defaultLayer, ...overrideLayers]));

    // Architect always sees everything
    if (!visibleIn.includes('Architect')) {
      visibleIn.push('Architect');
    }

    features.push({
      id: route.path,
      title,
      description,
      icon,
      category,
      defaultLayer,
      visibleIn,
      source,
      componentName: route.component,
      requiresLifeCriticalAuth: route.requiresLifeCriticalAuth,
      navItemIds: matchingNavs.map((n) => n.id),
    });
  }

  // Sort by category then title
  features.sort((a, b) => {
    const catCmp = a.category.localeCompare(b.category);
    if (catCmp !== 0) return catCmp;
    return a.title.localeCompare(b.title);
  });

  return features;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Feature Catalog hook.
 *
 * Usage:
 * ```tsx
 * const { features, toggleFeatureLayer, categories } = useFeatureCatalog();
 *
 * // Browse all features
 * features.map(f => <FeatureCard key={f.id} feature={f} />)
 *
 * // Toggle "show in Admin" for a feature
 * toggleFeatureLayer('/architect/federation', 'Admin', true);
 * ```
 */
export function useFeatureCatalog(): UseFeatureCatalogReturn {
  const { user, tenant } = useAuth();
  const tenantId = tenant?.id ?? null;
  const userId = user?.id ?? 'anonymous';
  const { config, loading } = useTenantSubgraphConfig(tenantId);

  // State for overrides (read from localStorage on mount)
  const [overridesState, setOverridesState] = useState<FeatureLayerOverrides | null>(() => {
    if (!tenantId) return null;
    return readOverrides(tenantId);
  });

  // Build feature list from config + overrides
  const features = useMemo(() => {
    if (!config) return [];
    return buildFeatures(
      config.allRoutes,
      config.allNavItems,
      config.subgraphs,
      overridesState,
    );
  }, [config, overridesState]);

  // Extract unique categories
  const categories = useMemo(() => {
    const catSet = new Set(features.map((f) => f.category));
    return Array.from(catSet).sort();
  }, [features]);

  // Count overrides
  const overrideCount = useMemo(() => {
    if (!overridesState) return 0;
    return Object.keys(overridesState.overrides).length;
  }, [overridesState]);

  // Toggle feature visibility in a layer
  const toggleFeatureLayer = useCallback(
    (featureId: string, layer: ViewLayer, enabled: boolean) => {
      if (!tenantId) return;

      const current = overridesState ?? {
        version: 1 as const,
        overrides: {},
        updatedAt: '',
        updatedBy: '',
      };

      const existing = current.overrides[featureId] ?? [];

      let updated: ViewLayer[];
      if (enabled) {
        // Add layer if not already present
        updated = Array.from(new Set([...existing, layer]));
      } else {
        // Remove layer
        updated = existing.filter((l) => l !== layer);
      }

      const newOverrides: FeatureLayerOverrides = {
        ...current,
        overrides: {
          ...current.overrides,
          [featureId]: updated,
        },
        updatedAt: new Date().toISOString(),
        updatedBy: userId,
      };

      // Clean up empty override entries
      if (updated.length === 0) {
        delete newOverrides.overrides[featureId];
      }

      // Write to localStorage and update state
      writeOverrides(tenantId, newOverrides);
      setOverridesState(newOverrides);
    },
    [tenantId, userId, overridesState],
  );

  // Check visibility
  const isVisibleInLayer = useCallback(
    (featureId: string, layer: ViewLayer): boolean => {
      const feature = features.find((f) => f.id === featureId);
      if (!feature) return false;
      return feature.visibleIn.some((l) => l.toUpperCase() === layer.toUpperCase());
    },
    [features],
  );

  // Reset all overrides
  const resetOverrides = useCallback(() => {
    if (!tenantId) return;
    clearOverrides(tenantId);
    setOverridesState(null);
  }, [tenantId]);

  return {
    features,
    categories,
    toggleFeatureLayer,
    isVisibleInLayer,
    resetOverrides,
    overrides: overridesState,
    loading,
    overrideCount,
  };
}

export default useFeatureCatalog;
